import { startTransition, useActionState, useEffect, useState } from 'react';
import Turnstile, { useTurnstile } from 'react-turnstile';
import {
  Anchor,
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDebouncedValue } from '@mantine/hooks';
import { zodResolver } from 'mantine-form-zod-resolver';
import {
  IconAt,
  IconLock,
  IconLockFilled,
  IconLockOpen2,
  IconLogin2,
  IconUser,
} from '@tabler/icons-react';
import { authentication } from '@/actions/authentication';
import { checkAvailableEmail, checkAvailableName } from '@/actions/available';
import { ResetFormSchema, SignInFormSchema, SignUpFormSchema } from '@/shared/libs/validations';
import { useUserStore } from '@/shared/stores/userStore';
import { useTranslation } from '@/i18n/client';
import classes from './AuthenticationForm.module.css';

export function AuthenticationForm() {
  const [type, setType] = useState<'login' | 'register' | 'reset'>('login');
  const [state, action, pending] = useActionState(authentication, undefined);
  const { setUser } = useUserStore((state) => state);
  const { t, lng } = useTranslation('auth');
  const turnstile = useTurnstile();

  const form = useForm({
    mode: 'controlled',
    clearInputErrorOnChange: true,
    validateInputOnChange: true,
    initialValues: {
      type,
      email: '',
      name: '',
      password: '',
      confirmPassword: '',
      terms: false,
      remember: true,
      token: '',
    },
    validate: zodResolver(
      type === 'reset' ? ResetFormSchema : type === 'login' ? SignInFormSchema : SignUpFormSchema,
    ),
  });

  const [debouncedName] = useDebouncedValue(form.values.name, 1000);
  const [debouncedEmail] = useDebouncedValue(form.values.email, 1000);

  useEffect(
    () => {
      if (pending) {
        form.setFieldValue('token', '');
        turnstile.reset();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pending, turnstile],
  );

  useEffect(
    () => {
      if (!pending && state?.errors) {
        if (state?.errors.name) {
          form.setFieldError('name', state.errors.name);
        }
        if (state?.errors.email) {
          form.setFieldError('email', state.errors.email);
        }
        if (state?.errors.password) {
          form.setFieldError('password', state.errors.password);
        }
        if (state?.errors.confirmPassword) {
          form.setFieldError('confirmPassword', state.errors.confirmPassword);
        }
        if (state?.errors.terms) {
          form.setFieldError('terms', state.errors.terms);
        }
        if (state?.errors.token) {
          form.setFieldError('token', state.errors.token);
        }
      }
      if (state?.user) {
        setUser(state.user);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pending, state],
  );

  useEffect(
    () => {
      form.clearErrors();
      form.setFieldValue('type', type);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type],
  );

  useEffect(
    () => {
      if (type !== 'register') {
        return;
      }
      const errorText = 'This name already registered.';
      if (!form.errors.name || form.errors.name === errorText) {
        checkAvailableName(debouncedName).then((result) => {
          if (!result) {
            form.setFieldError('name', errorText);
          } else {
            form.clearFieldError('name');
          }
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedName, type],
  );

  useEffect(
    () => {
      if (type !== 'register') {
        return;
      }
      const errorText = 'This email already registered.';
      if (!form.errors.email || form.errors.email === errorText) {
        checkAvailableEmail(debouncedEmail).then((result) => {
          if (!result) {
            form.setFieldError('email', errorText);
          } else {
            form.clearFieldError('email');
          }
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedEmail, type],
  );

  return (
    <Box>
      <Grid mb='md'>
        <Grid.Col span={{ base: 6 }}>
          <Button
            style={{ color: type === 'login' ? 'white' : 'gray' }}
            size='lg'
            color={type === 'login' ? 'rgb(60, 60, 120)' : 'rgb(20, 20, 50)'}
            fullWidth
            onClick={() => setType('login')}
          >
            {t('login')}
          </Button>
        </Grid.Col>
        <Grid.Col span={{ base: 6 }}>
          <Button
            style={{ color: type === 'register' ? 'white' : 'gray' }}
            size='lg'
            color={type === 'register' ? 'rgb(60, 60, 120)' : 'rgb(20, 20, 50)'}
            fullWidth
            onClick={() => setType('register')}
          >
            {t('register')}
          </Button>
        </Grid.Col>
      </Grid>
      <Divider my='md' />
      <form onSubmit={form.onSubmit((values) => startTransition(() => action(values)))}>
        <input type='hidden' id='type' name='type' value={type} />
        <Stack>
          {type === 'register' && (
            <TextInput
              key={form.key('name')}
              {...form.getInputProps('name')}
              required
              leftSection={<IconUser size={20} />}
              size='md'
              radius='md'
              placeholder={t('placeholders.name')}
              maxLength={10}
              error={form.errors.name && t(`errors.name.${form.errors.name}`)}
            />
          )}

          {type === 'reset' && (
            <Text size='md'>
              {t(
                'Enter the email address of the account for which you want to reset the password.',
              )}
            </Text>
          )}

          <TextInput
            key={form.key('email')}
            {...form.getInputProps('email')}
            required
            leftSection={<IconAt size={20} />}
            size='md'
            radius='md'
            placeholder={t('placeholders.email')}
            error={form.errors.email && t(`errors.email.${form.errors.email}`)}
          />

          {type !== 'reset' && (
            <PasswordInput
              key={form.key('password')}
              {...form.getInputProps('password')}
              required
              leftSection={<IconLock size={20} />}
              size='md'
              radius='md'
              placeholder={t('placeholders.password')}
              error={form.errors.password && t(`errors.password.${form.errors.password}`)}
            />
          )}

          {type !== 'reset' && type !== 'login' && (
            <PasswordInput
              key={form.key('confirmPassword')}
              {...form.getInputProps('confirmPassword')}
              required
              leftSection={<IconLockFilled size={20} />}
              size='md'
              radius='md'
              placeholder={t('placeholders.confirmPassword')}
              error={
                form.errors.confirmPassword &&
                t(`errors.confirmPassword.${form.errors.confirmPassword}`)
              }
            />
          )}

          {type === 'register' && (
            <Checkbox
              key={form.key('terms')}
              {...form.getInputProps('terms', { type: 'checkbox' })}
              size='md'
              label={t('labels.terms')}
              error={form.errors.terms && t(`errors.terms.${form.errors.terms}`)}
            />
          )}
        </Stack>

        {type === 'login' && (
          <Group justify='space-between' mt='lg'>
            <Checkbox
              key={form.key('remember')}
              {...form.getInputProps('remember', { type: 'checkbox' })}
              size='md'
              label={t('labels.remember')}
              error={form.errors.remember && t(`errors.terms.${form.errors.remember}`)}
            />
            <Anchor component='button' size='md' onClick={() => setType('reset')}>
              {t('forgot')}
            </Anchor>
          </Group>
        )}

        <Group justify='center' mt='xl'>
          <Turnstile
            sitekey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
            onVerify={(token) => form.setFieldValue('token', token)}
            onError={() => form.setFieldValue('token', '')}
            onExpire={() => form.setFieldValue('token', '')}
            onSuccess={(token: string) => form.setFieldValue('token', token)}
            language={lng}
          />
          <Text size='sm' className={classes.captchaError}>
            {state?.errors?.token && t(`errors.token.${state.errors.token}`)}
          </Text>
          <Text size='sm' className={classes.message}>
            {state?.message && t(`messages.${state.message}`)}
          </Text>
        </Group>

        <Group justify='space-between' mt='md'>
          <Button
            leftSection={
              type === 'login' ? (
                <IconLogin2 size={18} />
              ) : type === 'register' ? (
                <IconLogin2 size={18} />
              ) : (
                <IconLockOpen2 size={18} />
              )
            }
            type='submit'
            fullWidth
            size='md'
            disabled={form.values.token.length === 0}
            loading={pending}
            color='rgb(60, 60, 120)'
          >
            {t(`${type}`)}
          </Button>
        </Group>
      </form>
    </Box>
  );
}

export default AuthenticationForm;
